/**
 * Droppable Grid Cell Component
 *
 * Represents a time slot in the schedule grid that can receive dragged classes.
 * Uses @dnd-kit/core for droppable behavior.
 * Creates 15-minute sub-zones within each cell for precise drop positioning.
 */

import React from 'react';
import { Box } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';

interface DropSubZoneProps {
  day: string;
  subTime: string;
  isEditable: boolean;
  isFirstZone: boolean;
  children?: React.ReactNode;
}

const DropSubZone: React.FC<DropSubZoneProps> = ({
  day,
  subTime,
  isEditable,
  isFirstZone,
  children,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `cell-${day}-${subTime}`,
    disabled: !isEditable,
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        position: 'relative',
        bgcolor: isOver ? 'action.selected' : 'transparent',
        transition: 'background-color 0.2s ease',
        '&:hover': isEditable
          ? {
              bgcolor: isOver ? 'action.selected' : 'action.hover',
            }
          : {},
      }}
    >
      {/* Only render children in the first sub-zone to avoid duplication */}
      {isFirstZone && children}
    </Box>
  );
};

interface DroppableGridCellProps {
  day: string;
  time: string;
  isEditable: boolean;
  onClick: (day: string, time: string) => void;
  children: React.ReactNode;
}

const DroppableGridCell: React.FC<DroppableGridCellProps> = ({
  day,
  time,
  isEditable,
  onClick,
  children,
}) => {
  // Create 4 sub-zones for 15-minute intervals (0, 15, 30, 45)
  const [hours, minutes] = time.split(':').map(Number);
  const baseMinutes = hours * 60 + minutes;

  const subZones = [0, 15, 30, 45].map(offset => {
    const totalMinutes = baseMinutes + offset;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  });

  return (
    <Box
      onClick={() => onClick(day, time)}
      sx={{
        borderRight: 1,
        borderBottom: 1,
        borderColor: 'divider',
        minHeight: '60px',
        bgcolor: 'background.default',
        position: 'relative',
        cursor: isEditable ? 'pointer' : 'default',
      }}
    >
      {/* Absolute positioned layer for class blocks */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
        }}
      >
        {children}
      </Box>

      {/* Grid layer for drop zones */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: 'repeat(4, 1fr)',
          minHeight: '60px',
        }}
      >
        {subZones.map((subTime, index) => (
          <DropSubZone
            key={subTime}
            day={day}
            subTime={subTime}
            isEditable={isEditable}
            isFirstZone={index === 0}
          />
        ))}
      </Box>
    </Box>
  );
};

export default DroppableGridCell;
